import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { rewardsDistributorAbi } from "../abi";
import { useApiConfig, useRewards, useStats } from "../lib/api";
import { fmtEth, wei } from "../lib/format";
import { sendTx } from "../lib/tx";
import { ConnectButton } from "../components/Connect";
import { useWallet } from "../lib/wallet";
import { Countdown, nextPayoutTs } from "../components/Countdown";
import "./portfolio.css";
import "./rewards.css";

export function Rewards() {
  const conn = useWallet();
  const { data: cfg } = useApiConfig();
  const { data: stats } = useStats();
  const { data: rw } = useRewards(conn.address);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);

  const now = Date.now() / 1000;
  const epochs = rw?.epochs ?? [];
  // Claims open 2 hours after an epoch is posted.
  const claimable = epochs.filter((e) => !e.claimed && e.endTs + 2 * 3600 <= now);
  const pending = epochs.filter((e) => !e.claimed && e.endTs + 2 * 3600 > now);
  const claimableEth = claimable.reduce((a, e) => a + wei(e.amount), 0);
  const lifetime = epochs.reduce((a, e) => a + wei(e.amount), 0);

  async function claimAll() {
    if (!cfg || !conn.address || !claimable.length) return;
    setBusy(true);
    try {
      await sendTx(`Claim ${fmtEth(claimableEth)}`, {
        address: cfg.contracts.distributor,
        abi: rewardsDistributorAbi,
        functionName: "claimMany",
        args: [claimable.map((e) => BigInt(e.epoch)), conn.address, claimable.map((e) => BigInt(e.amount)), claimable.map((e) => e.proof)],
      });
      qc.invalidateQueries({ queryKey: ["rewards"] });
    } catch {
      /* reported by sendTx */
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="wrap page">
      <header className="page-head">
        <span className="eyebrow">Holder rewards</span>
        <h1>Hold $STRIKE. Get paid in ETH.</h1>
        <p>
          70% of the creator tax on every trade and of every options fee is paid out to holders twice a day, at 00:00 and 12:00 UTC,
          in proportion to your average balance over the previous 12 hours.
        </p>
      </header>

      <div className="rw-grid">
        <div className="panel rw-card">
          <span className="eyebrow">Next payout</span>
          <span className="rw-big num">
            <Countdown to={nextPayoutTs()} />
          </span>
          <span className="mute mono">{stats ? `${fmtEth(wei(stats.nextPayoutPot))} in the pot so far` : "—"}</span>
        </div>
        <div className="panel rw-card call-edge">
          <span className="eyebrow">You can claim</span>
          {conn.isConnected ? (
            <>
              <span className="rw-big num call">{fmtEth(claimableEth)}</span>
              <button className="btn btn-primary" disabled={!claimable.length || busy} onClick={claimAll}>
                {busy ? "Confirm in wallet…" : claimable.length > 1 ? `Claim all ${claimable.length} payouts` : "Claim"}
              </button>
            </>
          ) : (
            <>
              <span className="rw-big num faint">—</span>
              <ConnectButton />
            </>
          )}
        </div>
        <div className="panel rw-card">
          <span className="eyebrow">Paid to all holders</span>
          <span className="rw-big num">{stats ? fmtEth(wei(stats.paidToHolders) + wei(stats.allocatedUnclaimed)) : "—"}</span>
          <span className="mute mono">{conn.isConnected ? `you: ${fmtEth(lifetime)} lifetime` : "since launch"}</span>
        </div>
      </div>

      {conn.isConnected && epochs.length > 0 && (
        <div className="panel" style={{ marginTop: 16 }}>
          <div className="panel-head">
            <h3>Your payouts</h3>
          </div>
          <div className="rows">
            {epochs.map((e) => (
              <div className="row" key={e.epoch}>
                <div className="row-main">
                  <span className="pill">#{e.epoch}</span>
                  <span className="mono">{new Date(e.endTs * 1000).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short", timeZone: "UTC" })} UTC</span>
                </div>
                <span className="num call">{fmtEth(wei(e.amount))}</span>
                <span className={`pill ${e.claimed ? "" : "call"}`}>{e.claimed ? "claimed" : pending.includes(e) ? "opens soon" : "ready"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rw-how">
        <div>
          <span className="safe-n num">01</span>
          <h3>Average balance, not a snapshot.</h3>
          <p>Each payout uses your time-weighted balance over 12 hours. Buying a minute before and selling after earns almost nothing.</p>
        </div>
        <div>
          <span className="safe-n num">02</span>
          <h3>Covered calls still count.</h3>
          <p>$STRIKE you lock to sell covered calls keeps earning rewards, so you get paid twice: the premium and the payout.</p>
        </div>
        <div>
          <span className="safe-n num">03</span>
          <h3>$10 minimum, 60 days to claim.</h3>
          <p>Wallets averaging under $10 are skipped. Unclaimed ETH returns to the pot after 60 days. Every payout's data is public.</p>
        </div>
      </div>
    </section>
  );
}
